import React from 'react';
import { Chip, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { AxiosError, AxiosResponse } from 'axios';

import { RevisionContext } from '../RevisionContext';
import { BatchCorrectionRemove } from '../components/BatchCorrectionRemove';
import { BatchCorrectionAction } from '../enum/BatchCorrectionAction';
import { BatchCorrectionActions } from '../components/BatchCorrectionActions';
import { HomeContext } from '../../../Home/HomeContext';
import { ExpositionModel } from '../../../../Models/ExpositionModel';
import { theme } from '../../../../theme';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { ExpositionBatchModel, expositionBatchModelInitial } from '../../../../Models/ExpositionBatchModel';
import ClassificationExpositionService from '../../../../Services/ClassificationExpositionService';
import ExpositionService from '../../../../Services/ExpositionService';
import { BatchDto } from '../../../../Models/BatchDto';
import { BatchModel } from '../../../../Models/BatchModel';
import { BatchCorrectionContext } from '../BatchCorrectionContext';

export function ExpositionBatchCorrection() {

    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);
    const batchCorrectionContext = React.useContext(BatchCorrectionContext);

    const [expositionListDelete, setExpositionListDelete] = React.useState<BatchDto[]>([]);
    const [expositionDeleteSelected, setExpositionDeleteSelected] = React.useState<BatchModel[]>([]);

    const [expositionListAdd, setExpositionListAdd] = React.useState<ExpositionModel[]>([]);
    const [expositionAddSelected, setExpositionAddSelected] = React.useState<ExpositionModel[]>([]);

    const [expositionNotDefinedError, setExpositionNotDefinedError] = React.useState<boolean>(false);

    const [batchCorrectionAction, setBatchCorrectionAction] = React.useState<BatchCorrectionAction>(BatchCorrectionAction.ADD);

    React.useEffect(() => {
        const open = revisionContext.expositionBatchOpenDialog;
        setExpositionNotDefinedError(false);

        if (open && batchCorrectionAction === BatchCorrectionAction.ADD && expositionListAdd.length === 0)
            fetchMediaExpositionList();

        if (open && batchCorrectionAction === BatchCorrectionAction.REMOVE && expositionListDelete.length === 0)
            fetchExpositionList();
    }, [revisionContext.expositionBatchOpenDialog, batchCorrectionAction]);

    const fetchMediaExpositionList = () => {
        if (revisionContext.filterClassification.clientId)
            ExpositionService.getExpositionByMediaId(revisionContext.mediaId)
                .then((res: AxiosResponse) => setExpositionListAdd(res.data))
                .catch((error: AxiosError) => homeContext.showError('', error));
    };

    const fetchExpositionList = () => {
        const expositionBatch = expositionBatchModelInitial;
        expositionBatch.classificationsId = revisionContext.ids;

        ClassificationExpositionService.getExpositionBatch(expositionBatch)
            .then((res: AxiosResponse) => setExpositionListDelete(res.data.expositionsDto))
            .catch((error: AxiosError) => homeContext.showError('', error));
    };

    const handleSaveButton = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            const expositionBatch = { ...expositionBatchModelInitial };
            expositionBatch.classificationsId = revisionContext.ids;

            if (batchCorrectionAction === BatchCorrectionAction.ADD) {
                expositionBatch.expositions = expositionAddSelected;
                updateExpositionBatch(expositionBatch);
            } else {
                expositionBatch.expositions = expositionDeleteSelected;
                deleteExpositionBatch(expositionBatch);
            }
        }
    };

    const updateExpositionBatch = (expositionBatch: ExpositionBatchModel) => {
        ClassificationExpositionService.insertExpositionBatch(expositionBatch)
            .then(() => showSuccess())
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const deleteExpositionBatch = (expositionBatch: ExpositionBatchModel) => {
        ClassificationExpositionService.deleteExpositionBatch(expositionBatch)
            .then(() => showSuccess())
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const handleClearModal = () => {
        revisionContext.setExpositionBatchOpenDialog(false);
        setExpositionListAdd([]);
        setExpositionAddSelected([]);
        setExpositionListDelete([]);
        setExpositionDeleteSelected([]);
        setBatchCorrectionAction(BatchCorrectionAction.ADD);
        setExpositionNotDefinedError(false);
    };

    const hasError = () => {
        let error = false;

        if (batchCorrectionAction === BatchCorrectionAction.ADD)
            error = (!expositionAddSelected || expositionAddSelected.length == 0);

        setExpositionNotDefinedError(error);
        return error;
    };

    const getModal = () => {
        return (<Grid container spacing={3}>
            <BatchCorrectionActions
                value={batchCorrectionAction}
                setValue={setBatchCorrectionAction} />
            {batchCorrectionAction === BatchCorrectionAction.ADD && getExpositionAddInput()}
            {batchCorrectionAction === BatchCorrectionAction.REMOVE && getExpositionRemoveTags()}
        </Grid>
        );
    };

    const getExpositionRemoveTags = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant='subtitle1'>Excluir recursos multimídia</Typography>
                    <Typography variant='caption'>*Os recursos multimídia sem preenchimento não são comuns às classificações selecionadas</Typography>
                </Grid>
                <Grid item xs={12}>
                    <BatchCorrectionRemove
                        list={expositionListDelete}
                        setList={setExpositionListDelete}
                        removed={expositionDeleteSelected}
                        setRemoved={setExpositionDeleteSelected} />
                </Grid>
            </>
        );
    };

    const getExpositionAddInput = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id="sources"
                    options={expositionListAdd}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    filterSelectedOptions
                    value={expositionAddSelected}
                    onChange={(_, value: ExpositionModel[]) => setExpositionAddSelected(value)}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Fontes"
                            fullWidth
                            error={expositionNotDefinedError && (!expositionAddSelected || expositionAddSelected.length == 0)}
                            helperText={expositionNotDefinedError && (!expositionAddSelected || expositionAddSelected.length == 0) ? 'Selecione pelo menos uma fonte' : ''}
                        />
                    )}
                />
            </Grid>
        );
    };

    const showSuccess = () => {
        handleClearModal();
        homeContext.showSuccess(`As alterações foram realizadas para todas as ${revisionContext.ids.length} classificações selecionadas com sucesso!`);
        batchCorrectionContext.setCallSearch(true);
    };

    return (
        <BasicConfirmDialog
            title={'Alteração de recursos multimídia'}
            description={'Exclua ou adicione recursos multimídia para as classificações selecionadas'}
            handleFirstButton={() => handleClearModal()}
            handleSecondButton={() => handleSaveButton()}
            textFirstButton='DESCARTAR'
            textSecondButton='SALVAR'
            open={revisionContext.expositionBatchOpenDialog}
            children={getModal()}
            cssProperties={{
                minWidth: '800px',
                maxWidth: '800px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }}

        />
    );
}

